/**
 * EventAttendanceQuickEditDialog — モニタリング画面からイベント参加実数を即時編集する小ダイアログ。
 *
 * - 3 つの入力欄（会場 / オンライン / アーカイブ）と合計表示のみ
 * - 保存時に events/{id} の attendanceBreakdown + currentAttendees を更新
 * - 保存後は ['campaign_funnel', campaignId] と ['events', ...] を invalidate
 *
 * フル編集が必要な場合は LINE Bot → イベント の EventFormDialog を案内。
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useUpdateEvent } from '@/hooks/useLineData';
import { db } from '@/lib/firebase';

import type { Event as EventDoc } from '@goenhub/shared';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** events/{id} */
  eventId: string | null;
  /** 表示用ラベル（カードの refLabel） */
  eventLabel?: string | null;
  /** 保存後に再フェッチさせたい campaignId */
  campaignId?: string | null;
}

export function EventAttendanceQuickEditDialog({
  open,
  onOpenChange,
  eventId,
  eventLabel,
  campaignId,
}: Props) {
  const updateMutation = useUpdateEvent();
  const qc = useQueryClient();

  // 現在値を都度取得（編集ダイアログを開いた瞬間の値）
  const { data: ev, isLoading } = useQuery({
    queryKey: ['event_quick_edit', eventId],
    enabled: !!eventId && open,
    queryFn: async (): Promise<EventDoc | null> => {
      if (!eventId) return null;
      const snap = await getDoc(doc(db, 'events', eventId));
      if (!snap.exists()) return null;
      return snap.data() as EventDoc;
    },
    staleTime: 0,
  });

  const [onsite, setOnsite] = useState(0);
  const [online, setOnline] = useState(0);
  const [archive, setArchive] = useState(0);

  useEffect(() => {
    if (ev) {
      setOnsite(ev.attendanceBreakdown?.onsite ?? 0);
      setOnline(ev.attendanceBreakdown?.online ?? 0);
      setArchive(ev.attendanceBreakdown?.archive ?? 0);
    } else if (open && !isLoading) {
      // 取得失敗 / 未紐付け
      setOnsite(0);
      setOnline(0);
      setArchive(0);
    }
  }, [ev, open, isLoading]);

  const total = (onsite || 0) + (online || 0) + (archive || 0);

  const handleSave = async () => {
    if (!eventId) return;
    try {
      await updateMutation.mutateAsync({
        id: eventId,
        data: {
          attendanceBreakdown: {
            onsite: Math.max(0, Number(onsite) || 0),
            online: Math.max(0, Number(online) || 0),
            archive: Math.max(0, Number(archive) || 0),
          },
          currentAttendees: total,
        },
      });
      toast.success('参加実数を更新しました');
      // モニタリングのファンネルとイベント一覧を invalidate
      if (campaignId) {
        qc.invalidateQueries({ queryKey: ['campaign_funnel', campaignId] });
      }
      qc.invalidateQueries({ queryKey: ['events'] });
      qc.invalidateQueries({ queryKey: ['event_quick_edit', eventId] });
      onOpenChange(false);
    } catch (err) {
      toast.error(
        `保存に失敗しました: ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>参加実数を更新</DialogTitle>
          <DialogDescription>
            {eventLabel ? `「${eventLabel}」` : '選択イベント'}{' '}
            の参加実数を手入力します。CV 率（このイベント → 下流フォーム等）の分母に使われます。
          </DialogDescription>
        </DialogHeader>

        {!eventId ? (
          <div className="text-sm text-[var(--text-muted)] py-4">
            イベントが紐付けられていません。先にカードをクリックして紐付けてください。
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-6">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label htmlFor="qa-onsite">会場</Label>
                <Input
                  id="qa-onsite"
                  type="number"
                  min={0}
                  value={onsite}
                  onChange={(e) => setOnsite(Number(e.target.value) || 0)}
                />
              </div>
              <div>
                <Label htmlFor="qa-online">オンライン</Label>
                <Input
                  id="qa-online"
                  type="number"
                  min={0}
                  value={online}
                  onChange={(e) => setOnline(Number(e.target.value) || 0)}
                />
              </div>
              <div>
                <Label htmlFor="qa-archive">アーカイブ</Label>
                <Input
                  id="qa-archive"
                  type="number"
                  min={0}
                  value={archive}
                  onChange={(e) => setArchive(Number(e.target.value) || 0)}
                />
              </div>
            </div>
            <div className="text-sm pt-2 border-t border-[var(--border)]">
              合計（参加実数）:{' '}
              <span className="font-semibold tabular-nums">{total}</span> 名
            </div>
            <p className="text-[11px] text-[var(--text-muted)]">
              名前・日時など他の情報を編集する場合は、LINE Bot → イベント管理から行ってください。
            </p>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={updateMutation.isPending}
          >
            キャンセル
          </Button>
          <Button
            type="button"
            variant="primary"
            onClick={handleSave}
            disabled={!eventId || updateMutation.isPending || isLoading}
          >
            {updateMutation.isPending ? '保存中...' : '保存'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
